import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  EmojiEvents as RankingIcon,
  EmojiEvents as ContestIcon,
  WorkspacePremium as CertificateIcon,
  School as LearningIcon,
  Description as DescriptionIcon,
  Compare as CompareIcon,
  AutoAwesome as RecommendationsIcon,
  Timeline as TimelineIcon,
  KeyboardDoubleArrowLeft as CollapseIcon,
  KeyboardDoubleArrowRight as ExpandIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';
import { useNavigate, useLocation } from 'react-router-dom';
import EncycdataLogo from '@components/layout/EncycdataLogo';

/**
 * Shared player portal sidebar: same 9 items in same order on every player page.
 * Labels avoid "My" prefixes for cleaner navigation.
 */
interface PlayerSidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showCollapseToggle?: boolean;
}

const PlayerSidebar: React.FC<PlayerSidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
  showCollapseToggle = false,
}) => {
  const { logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const is = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isCoursesView = location.pathname.startsWith('/player/learning/courses') || location.pathname === '/player/learning';
  const isRoadmapView = location.pathname.startsWith('/player/learning/roadmap') || new URLSearchParams(location.search).get('view') === 'roadmap';

  const link = (path: string, label: string, Icon: React.ComponentType<{ sx?: any }>, extraMatch?: boolean) => {
    const selected = extraMatch ? is(path) || location.pathname.includes(path) : is(path);
    return (
      <ListItem disablePadding key={path}>
        <ListItemButton
          selected={selected}
          onClick={() => navigate(path)}
          sx={{
            color: '#ecf0f1',
            bgcolor: selected ? '#34495e' : 'transparent',
            '&:hover': { bgcolor: '#34495e' },
            '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
          }}
        >
          <ListItemIcon sx={{ color: selected ? '#ff9800' : '#ecf0f1', minWidth: 56 }}>
            <Icon />
          </ListItemIcon>
          {!collapsed && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: selected ? 700 : 600,
                color: selected ? '#ff9800' : '#ecf0f1',
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={{ bgcolor: '#2c3e50', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ bgcolor: '#A05AFF', minHeight: '48px !important', gap: 1 }}>
        <EncycdataLogo height={28} lightBackground sx={{ mr: 1 }} />
        {!collapsed && (
          <Typography variant="h6" noWrap component="div" sx={{ color: '#ffffff', fontWeight: 700 }}>
            Player Portal
          </Typography>
        )}
        {showCollapseToggle && onToggleCollapse && (
          <IconButton
            size="small"
            onClick={onToggleCollapse}
            sx={{ ml: 'auto', color: '#ffffff' }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
          </IconButton>
        )}
      </Toolbar>
      <List sx={{ flexGrow: 1 }}>
        {link('/player/dashboard', 'Dashboard', DashboardIcon)}
        {link('/player/profile', 'Profile', PersonIcon)}
        {link('/player/learning/courses', 'Learning', LearningIcon, true)}
        {!collapsed && <ListItem disablePadding>
          <ListItemButton
            selected={isCoursesView}
            onClick={() => navigate('/player/learning/courses')}
            sx={{
              color: '#ecf0f1',
              pl: 7,
              bgcolor: isCoursesView ? '#34495e' : 'transparent',
              '&:hover': { bgcolor: '#34495e' },
              '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
            }}
          >
            <ListItemIcon sx={{ color: isCoursesView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
              <LearningIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Courses"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: isCoursesView ? 700 : 600,
                color: isCoursesView ? '#ff9800' : '#ecf0f1',
              }}
            />
          </ListItemButton>
        </ListItem>}
        {!collapsed && <ListItem disablePadding>
          <ListItemButton
            selected={isRoadmapView}
            onClick={() => navigate('/player/learning/roadmap')}
            sx={{
              color: '#ecf0f1',
              pl: 7,
              bgcolor: isRoadmapView ? '#34495e' : 'transparent',
              '&:hover': { bgcolor: '#34495e' },
              '&.Mui-selected': { bgcolor: '#34495e', color: '#ff9800', '&:hover': { bgcolor: '#3d566e' } },
            }}
          >
            <ListItemIcon sx={{ color: isRoadmapView ? '#ff9800' : '#ecf0f1', minWidth: 36 }}>
              <TimelineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Roadmap"
              primaryTypographyProps={{
                fontSize: '0.85rem',
                fontWeight: isRoadmapView ? 700 : 600,
                color: isRoadmapView ? '#ff9800' : '#ecf0f1',
              }}
            />
          </ListItemButton>
        </ListItem>}
        {link('/player/ranking-overview', 'Learn and Rank', RankingIcon)}
        {link('/player/contests', 'Contests', ContestIcon)}
        {link('/player/performance', 'Training Evaluation', AssessmentIcon, true)}
        {link('/player/my-reports', 'Reports', DescriptionIcon)}
        {link('/player/recommendations', 'AI Assistant', RecommendationsIcon)}
        {link('/player/compare-peers', 'Peer Benchmark', CompareIcon)}
        {link('/player/certificates', 'Certificates', CertificateIcon)}
      </List>
      <Box sx={{ mt: 'auto', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ color: '#ff9800', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.1)' } }}>
            <ListItemIcon sx={{ color: '#ff9800' }}><LogoutIcon /></ListItemIcon>
            {!collapsed && (
              <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, color: '#ff9800' }} />
            )}
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );
};

export default PlayerSidebar;
